import _defineProperty from '@babel/runtime-corejs3/helpers/esm/defineProperty';
import * as React from 'react';
import classnames from 'classnames';
import { createFocusTrap } from 'focus-trap';
import { jsx } from 'react/jsx-runtime';

const focusTrapClassName = 'bui-focus-trap';

/**
 * A component which creates a focus loop within its children.
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { FocusTrap } from '@thisisbud/bui-react-focus-trap'
 * import { AlertDialog } from '../dialog'
 *
 * function ExampleComponent() {
 *   return <FocusTrap><AlertDialog/></FocusTrap>
 * }
 * ```
 */
class FocusTrap extends React.Component {
  /**
   * The component name displayed in the developer tools.
   */

  constructor(props) {
    super(props);
    _defineProperty(this, "contentRef", void 0);
    _defineProperty(this, "trap", void 0);
    this.contentRef = /*#__PURE__*/React.createRef();
  }

  /**
   * Activate the focus trap when the component mounts.
   */
  componentDidMount() {
    const contentElement = this.contentRef.current;
    const trap = createFocusTrap(contentElement, {
      allowOutsideClick: true,
      escapeDeactivates: true,
      fallbackFocus: contentElement,
      initialFocus: contentElement
    });
    trap.activate();
    this.trap = trap;
  }

  /**
   * Deactivate the focus trap before unmounting the component.
   */
  componentWillUnmount() {
    var _this$trap;
    (_this$trap = this.trap) === null || _this$trap === void 0 ? void 0 : _this$trap.deactivate();
  }

  /**
   * Reference to the element which contains the focus trap.
   */

  /**
   * Render the component.
   */
  render() {
    const {
      children,
      className
    } = this.props;
    return /*#__PURE__*/jsx("div", {
      className: classnames(focusTrapClassName, className),
      ref: this.contentRef,
      tabIndex: -1,
      children: children
    });
  }
}
_defineProperty(FocusTrap, "displayName", 'FocusTrap');

export { FocusTrap, focusTrapClassName };
